<template>
  <div
    class="rechercheViewWrapper"
    ref="rechercheViewWrapper"
  >
    <div class="searchWrapper searchWrapper--desktop">
      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        class="searchInput"
        size="large"
        state="default"
        placeholder="Référence virement, Facture…"
        :maxlength="80"
        deletable
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
      />
      <div class="searchWrapper--actions">
        <BasicTooltip
          label="Exporter les virements"
          position="left"
        >
          <BasicButton
            @click="exporter"
            size="large"
            icon-name="export"
            variant="outlined"
          />
        </BasicTooltip>
      </div>
    </div>
    <div class="searchWrapper searchWrapper--mobile">
      <div class="flex flex-gap-4">
        <BasicInput
          v-model="search"
          iconName="search"
          iconState="iconLeft"
          size="large"
          state="default"
          placeholder="Référence, Numéro Dossier..."
          :maxlength="80"
          style="width: 100%"
        />
        <BasicButton
          @click="exporter"
          label="Exporter"
          size="large"
          icon-name="export"
          variant="outlined"
        />
      </div>
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
        width="full"
      />
    </div>
    <Transition>
      <div
        class="filtersWrapper"
        v-if="showFilters"
      >
        <WrapperDropdown
          v-model="periodeDateEmission"
          label="Période de paiement"
          :items="periodeDateEmissionItems"
          placeholder="Sélection"
          class="col-12 col-md-18 col-sm-36"
        />
        <div class="col-4 col-sm-36 actions--item">
          <BasicTooltip label="Ajouter les filtres aux favoris">
            <BasicButton
              :disabled="!isAdvancedFiltered"
              type="secondary"
              iconName="star"
              @click="addFavori"
              style="display: inline"
            />
          </BasicTooltip>
          <BasicTooltip label="Supprimer les filtres">
            <BasicButton
              @click="removeFilters"
              :disabled="!isAdvancedFiltered"
              type="reverse"
              iconName="filter-remove"
              style="display: inline"
            />
          </BasicTooltip>
        </div>
      </div>
    </Transition>
    <div class="informationWrapper">
      <BasicPagination
        :nbPages="pageCount"
        :nbPagesMax="10"
        :currentPage="pageNumber"
        :nbResults="store.pageData?.totalRecords"
        @change="pageChange"
      />
      <BasicDropdown
        v-if="pageSizeVisible"
        v-model="pageSize"
        :items="pageSizeItems"
        placeholder="Nombre de résultats"
        width="auto"
        force-value
      />
    </div>
    <div
      class="listComponentMainWrapper"
      ref="listComponentMainWrapper"
    >
      <div class="mobileWrapper">
        <VirementCardMobile
          v-for="(item, index) in store.pageData?.data"
          :key="index"
          :item
        />
      </div>
      <div class="desktopWrapper">
        <VirementHeader v-model="order" />
        <VirementCardDesktop
          v-for="(item, index) in store.pageData?.data"
          class="listCardDesktopWrapper"
          :key="index"
          :item
        />
      </div>
      <cards-layout
        v-if="!store.pageData?.data.length"
        cardsType="Virements"
        headerText="Virements"
        :hasHeader="false"
        :cards="[]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onBeforeUnmount } from 'vue'
  import { onBeforeRouteUpdate } from 'vue-router'
  import { PeriodeLibelle } from '@/features/domaine/types/opco/shared/Periode'
  import { objectToDropdownItems } from '../../shared/tools'
  import { pageSizeItems } from '../shared/dropdown-items.ts'
  import { VirementCardDesktop, VirementCardMobile, VirementHeader } from './components'
  import { useRechercheVirementActions } from './useRechercheVirementActions'
  import { useRechercheVirementStore } from './useRechercheVirementStore'
  import CardsLayout from '@/features/interface/home/tableauDeBord/cardsLayout/CardsLayout.vue'
  import { type NavigateToQueryVirements, type WithNavigateToLocation } from '@/router/navigateTo'
  import { storeToRefs } from 'pinia'
  import { usePagination, useShowFilter } from '@/features/interface/recherches/shared'

  const store = useRechercheVirementStore()
  onBeforeUnmount(store.$reset)
  const { setupFieldWithKey, exportData } = store
  const { order, search } = storeToRefs(store)
  const { showFilters, removeFilters, isAdvancedFiltered } = useShowFilter(store)
  const { pageCount, pageSize, pageNumber, pageChange, pageSizeVisible } = usePagination(store)

  const periodeDateEmissionItems = objectToDropdownItems(PeriodeLibelle)
  const { addFavori } = useRechercheVirementActions()
  defineProps<WithNavigateToLocation<NavigateToQueryVirements>>()

  const periodeDateEmission = setupFieldWithKey<string>('query.periodeDateEmission')

  /* Exporter les virements */
  const exporter = () =>
    exportData({
      title: 'Export des virements',
      message: 'Les virements ont été exportés avec succès, vous allez les recevoir par mail',
    })

  onBeforeRouteUpdate((to, ...args) => {
    return to.meta.navigateToHelper?.beforeEnter?.call(null, to, ...args)
  })
</script>

<style scoped lang="less">
  @import '../index.less';
</style>
